import React, { useEffect, useState, useLayoutEffect } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import TournamentMatchesTable from "Components/Tournament/TournamentMatchesTable";
import StatsTable from "Components/Tournament/StatsTable";
import CreateMatchesButton from "Components/Tournament/CreateMatchesButton";
import CreateCrosspoolMatchesButton from "Components/Tournament/CreateCrosspoolMatchesButton";
import DeleteMatchesButton from "Components/Tournament/DeleteMatchesButton";
import { canManage } from "services/tournamentService";
import { useAuth } from "context/AuthProvider";
import { getMatchesByTournamentId } from "services/matchService";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import * as Sentry from "@sentry/react";
import EliminationPhaseManager from "Components/Tournament/EliminationPhaseManager";
import EliminationMatchesTable from "Components/Tournament/EliminationMatchesTable";
import EliminationBracket from "Components/Tournament/EliminationBracket";
import CompileTournament from "Components/Tournament/CompileTournament";
import TournamentPodium from "Components/Tournament/tournamentPodium";
import RandomScoreGenerator from "Components/Tournament/RandomScoreGenerator";
import { fetchLocationById } from "services/locationService";

const TournamentProgressPage = () => {
  const { user, loading: authLoading } = useAuth();
  const { tournamentId } = useParams();
  const [tournament, setTournament] = useState(null);
  const [location, setLocation] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [tournamentLoading, setTournamentLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(true);
  const navigate = useNavigate();
  const [scrollYBeforeRefresh, setScrollYBeforeRefresh] = useState(null);

  const reloadMatches = () => {
    setScrollYBeforeRefresh(window.scrollY); // Save scroll before refreshing
    setRefreshKey((prev) => prev + 1);
  };

  useLayoutEffect(() => {
    if (!matchesLoading && scrollYBeforeRefresh !== null) {
      window.scrollTo({ top: scrollYBeforeRefresh, behavior: "auto" });
      setScrollYBeforeRefresh(null); // Reset after scrolling
    }
  }, [matchesLoading, scrollYBeforeRefresh]);

  useEffect(() => {
    const fetchTournament = async () => {
      try {
        const snapshot = await getDoc(doc(db, "tournaments", tournamentId));
        if (snapshot.exists()) {
          setTournament({ id: snapshot.id, ...snapshot.data() });
        }
      } catch (error) {
        Sentry.captureException(error);
        console.error("Erreur lors du chargement du tournoi :", error);
      } finally {
        setTournamentLoading(false);
      }
    };

    fetchTournament();
  }, [tournamentId]);

  useEffect(() => {
    const fetchLocation = async () => {
      if (tournament?.locationId) {
        try {
          const loc = await fetchLocationById(tournament.locationId);
          setLocation(loc);
        } catch (error) {
          Sentry.captureException(error);
          console.error("Erreur lors du chargement du lieu :", error);
        }
      }
    };

    fetchLocation();
  }, [tournament?.locationId, user]);

  useEffect(() => {
    if (tournament && location) {
      setIsAdmin(canManage(user, location.id));
    }
  }, [tournament, location, user]);

  useEffect(() => {
    const fetchMatches = async () => {
      if (!tournament?.id) return;
      setMatchesLoading(true);
      try {
        const fetchedMatches = await getMatchesByTournamentId(tournament.id);
        setMatches(fetchedMatches);
      } catch (error) {
        Sentry.captureException(error);
        console.error("Erreur lors du chargement des matchs :", error);
      } finally {
        setMatchesLoading(false);
      }
    };

    fetchMatches();
  }, [tournament?.id, refreshKey]);

  if (authLoading || tournamentLoading || matchesLoading) {
    return <p className="text-center text-slate-400">Chargement...</p>;
  }

  if (!tournament) {
    return <p className="text-center text-red-500">Tournoi introuvable.</p>;
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 w-full mx-auto space-y-8 flex flex-col items-center">
      <div className="mx-auto w-3/5 mt-8">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-200 transition"
        >
          <ArrowLeft size={18} />
          Retour
        </button>
      </div>
      {isAdmin && (
        <>
          <div className="flex flex-wrap gap-2">
            {!matches.length > 0 && (
              <>
                <CreateMatchesButton
                  tournament={tournament}
                  onCreated={reloadMatches}
                />
                <CreateCrosspoolMatchesButton
                  tournament={tournament}
                  location={location}
                  onCreated={reloadMatches}
                />
              </>
            )}
            {matches.length > 0 && !tournament.compiled && (
              <DeleteMatchesButton
                tournament={tournament}
                onDeleted={reloadMatches}
              />
            )}
          </div>

          <h2 className="text-2xl font-bold">
            Matchs – {tournament.name || "Tournoi"}
          </h2>

          {/* Temporary Random Score Generator */}
          {location.testEnv && (
            <RandomScoreGenerator
              tournament={tournament}
              matches={matches}
              onScoresGenerated={reloadMatches}
            />
          )}

          <TournamentMatchesTable
            matches={matches}
            setMatches={setMatches}
            tournament={tournament}
          />
          <StatsTable matches={matches} teams={tournament.teams} tournament={tournament} />

          

          <div className="w-full flex flex-col items-center bg-slate-800 p-4 gap-8">
            <EliminationPhaseManager
              tournament={tournament}
              location={location}
              matches={matches}
              reloadMatches={reloadMatches}
            />
            <EliminationMatchesTable
              matches={matches}
              setMatches={setMatches}
              tournament={tournament}
              reloadMatches={reloadMatches}
            />
            <EliminationBracket matches={matches} tournament={tournament} />
            {isAdmin && <TournamentPodium tournament={tournament} />}
          </div>
          {isAdmin && (
            <div className="flex flex-col items-center p-4 w-full">
              <CompileTournament tournament={tournament} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TournamentProgressPage;
